import React, { useEffect, useState, useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';
import ReactTooltip, { Place, Type } from 'react-tooltip';

import styles from './styles.module.css';

const DEFAULT_TOOLTIP_ID = 'sp360-root-tooltip_id';

/**
 * A wrapper component that conditionally renders a tooltip around its children.
 * If a `tooltip` prop is provided, it wraps the children with a `Tooltip` component.
 * Otherwise, it renders the children directly without a tooltip.
 *
 * @param {Object} props - The props for the component.
 * @param {string} props.tooltip - The tooltip text to display. If not provided, no tooltip is rendered.
 * @param {React.ReactNode} props.children - The child elements to be wrapped by the tooltip or rendered directly.
 *
 * @returns {JSX.Element} The children wrapped with a tooltip if `tooltip` is provided, or the children directly.
 */
interface ConditionalTooltipWrapperProps {
  tooltip?: React.ReactNode;
  children: React.ReactNode;
  className?: string; // Add className prop
}

export const ConditionalTooltipWrapper: React.FC<
  ConditionalTooltipWrapperProps
> = ({ tooltip, children, className = '' }) => {
  useEffect(() => {
    if (tooltip) ReactTooltip.rebuild();
  }, [tooltip]);

  return tooltip ? (
    <Tooltip tooltip={tooltip} className={className}>
      {children}
    </Tooltip>
  ) : (
    <span className={className}>{children}</span> // Apply className even without tooltip
  );
};

interface TooltipProps {
  id?: string;
  tooltip: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  showIcon?: boolean;
  title?: React.ReactNode;
  type?: Type;
  place?: Place;
  iconClassName?: string;
  rebuildOnHover?: boolean;
}

/**
 * Tooltip Component
 *
 * Component for rendering a tooltip.
 *
 * NOTE: id value for Tooltip and, data-for attribute need to be the same
 * as shown in the example provided below.
 *
 * USAGE:
 * <span
 *      data-tip={TooltipContent({contents: 'sample tooltip text', title: 'my tooltip title'})}
 *      data-for='datagrid-text-tooltip'
 * >
 *  my text
 * </span>
 * <Tooltip id='datagrid-text-tooltip' />
 *
 * @param id The unique identifier for the tooltip.
 * @param className Optional. Additional CSS classes to apply to the tooltip.
 * @param type Optional. The type of tooltip (e.g., 'light', 'dark').
 * @param place Optional. The placement of the tooltip (e.g., 'top', 'bottom').
 * @returns The React component for the tooltip.
 */
export const Tooltip: React.FC<TooltipProps> = ({
  id,
  children,
  tooltip,
  title,
  className,
  showIcon = true,
  type = 'light',
  place = 'bottom',
  iconClassName = 'fa fa-circle-info',
  rebuildOnHover = false,
}: TooltipProps) => {
  // Rebuild the tooltip when the component mounts
  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  useEffect(() => {
    if (rebuildOnHover) {
      ReactTooltip.rebuild();
    }
  }, [rebuildOnHover]);

  if (!id) id = DEFAULT_TOOLTIP_ID;

  const [isHovered, setIsHovered] = useState(false);

  // Handle mouse enter event
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  // Handle mouse leave event
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Memoize the tooltip HTML content to avoid unnecessary re-renders
  const tooltipHtmlContents = useMemo(() => {
    const html = (
      <div className={styles.tooltipWrapper}>
        <div className={styles.background2}>
          <div className={styles.content}>
            {title && (
              <div className={styles.tooltipTitle}>
                {showIcon && (
                  <i
                    className={`${iconClassName} ${styles.tooltipIcon}`}
                    style={{ fontSize: '16px' }}
                  />
                )}
                <strong>{title}</strong>
              </div>
            )}
            <div className={styles.tooltipContent}>
              {!title && showIcon && (
                <i
                  className={`${iconClassName} ${styles.tooltipIcon}`}
                  style={{ fontSize: '16px' }}
                />
              )}
              {tooltip}
            </div>
          </div>
        </div>
      </div>
    );
    return ReactDOMServer.renderToStaticMarkup(html);
  }, [tooltip, title, showIcon, iconClassName]);

  return (
    <span
      data-tip={tooltipHtmlContents}
      data-for={id}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-describedby={id}
    >
      {children}
      {isHovered && (
        <ReactTooltip
          id={id}
          getContent={(dataTip) => dataTip}
          html={true}
          type={type}
          className={styles.tooltip}
          place={place}
          multiline={true}
          arrowColor="black"
        />
      )}
    </span>
  );
};

export default Tooltip;
